import { ArrowLeft, LogOut, ShieldAlert } from "lucide-react";
import { Brand, Button, LoginScreen, Panel, useAuth } from "@ozpanel/ui";

// Gate — decides what a session is allowed to see, and CRITICALLY never renders a state with
// no way out of it.
//
// The first version refused any non-super_admin with a bare sentence and no controls at all.
// Combined with the "Sign in as" (impersonate) action, that produced a genuine trap, hit for
// real: impersonating a tenant swaps the session for a lower-privileged principal, the
// console then refused it, and the screen offered no sign-out and no way to stop
// impersonating. The operator was locked out of their own admin console by clicking a button
// the console itself offered them.
//
// Two rules this leaves behind:
//   1. Every refusal screen carries an escape (sign out, and stop-impersonating when that is
//      what got you here).
//   2. An impersonated session is detected explicitly and handled BEFORE the role check, so
//      the message says what actually happened rather than "you are not an administrator".
//   3. It offers the console the impersonated account ACTUALLY USES. Rule 2 stopped the lie but
//      left a dead end: the screen said "the admin console is not available" and gave only
//      "return" and "sign out", so the one thing the operator wanted — to see what the tenant
//      sees — was the one thing it did not offer. Impersonating and then being told to go back
//      reads as the feature being broken, which is exactly how it was reported.

export function Gate({ children }: { children: React.ReactNode }) {
  const { who, loading, impersonating, stopImpersonating, logout } = useAuth();

  if (loading) {
    return <div className="grid min-h-dvh place-items-center text-[13px] text-faint">Loading…</div>;
  }

  if (!who) {
    // Recovery mode is linked from here because this is where a database-less panel leaves the
    // operator: signing in needs the database, and the recovery console does not.
    return (
      <LoginScreen
        title="Sign in"
        subtitle="Platform administration"
        footer={
          <>
            Database down on this node?{" "}
            <a className="text-accent hover:underline" href={`${import.meta.env.BASE_URL}recovery`}>
              Open recovery mode
            </a>
            <span className="mx-1.5 text-faint">·</span>
            <a className="text-accent hover:underline" href={`${import.meta.env.BASE_URL}install`}>
              Install progress
            </a>
          </>
        }
      />
    );
  }

  // Impersonation first: this is a recoverable state, not a permission failure, and saying
  // "you are not an administrator" to an administrator who is mid-impersonation is a lie.
  if (impersonating) {
    // Which console this account actually uses. The point of impersonating is to SEE what they
    // see, so the primary action opens it rather than sending the operator back where they came
    // from — that was offered as the only option and read as the feature not working.
    // A role with no console of its own is possible (an operator impersonating another
    // super_admin), so the link is offered only when there is one to offer. Returning to your
    // own account is always available either way.
    const target = consoleForRole(who.role);
    return (
      <Notice
        icon={<ArrowLeft size={18} />}
        title={`You are signed in as ${who.impersonating ?? who.email}`}
        body={
          <>
            Your own account is <span className="text-fg">{who.impersonated_by}</span>. The admin
            console is not available while acting as another account
            {target ? (
              <>
                {" "}— open <span className="text-fg">{target.label}</span> to see exactly what
                they see
              </>
            ) : null}
            . Everything you do is recorded against your own account.
          </>
        }
        primary={
          target
            ? { label: `Open ${target.label}`, onClick: () => window.location.assign(target.href) }
            : { label: "Return to my account", onClick: () => void stopImpersonating() }
        }
        secondary={target ? { label: "Return to my account", onClick: () => void stopImpersonating() } : undefined}
        tertiary={{ label: "Sign out", onClick: () => void logout() }}
      />
    );
  }

  if (who.role !== "super_admin") {
    // Same courtesy for someone who simply signed in at the wrong door: name the console their
    // role does use and take them to it, rather than telling them what they are not.
    const target = consoleForRole(who.role);
    return (
      <Notice
        icon={<ShieldAlert size={18} />}
        title="This console is for platform administrators"
        body={
          <>
            Signed in as <span className="text-fg">{who.email}</span> ({who.role}). Your account
            does not have platform administration access.
          </>
        }
        primary={target ? { label: `Open ${target.label}`, onClick: () => window.location.assign(target.href) } : undefined}
        secondary={{ label: "Sign out", onClick: () => void logout() }}
      />
    );
  }

  return <>{children}</>;
}

// consoleForRole maps a role to the console that role actually uses.
//
// One place, because the server's "/" handler already makes the same decision for v1 and two
// copies of a routing table drift. If a role has no console of its own the caller falls back to
// sign-out, which is why this returns null rather than guessing.
function consoleForRole(role: string): { label: string; href: string } | null {
  switch (role) {
    case "domain_admin":
      return { label: "the customer console", href: "/account/" };
    case "domain_user":
      return { label: "the mailbox console", href: "/ozpanel/" };
    default:
      return null;
  }
}

function Notice({
  icon,
  title,
  body,
  primary,
  secondary,
  tertiary,
}: {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  primary?: { label: string; onClick(): void };
  secondary?: { label: string; onClick(): void };
  tertiary?: { label: string; onClick(): void };
}) {
  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <div className="w-full max-w-[440px]">
        <div className="mb-7 flex justify-center">
          <Brand />
        </div>
        <Panel className="bg-gradient-to-b from-surface to-bg2 p-6 text-center">
          <div className="mx-auto mb-3.5 grid size-10 place-items-center rounded-full border border-line bg-surface2 text-accent">
            {icon}
          </div>
          <h1 className="text-[16px] font-[680] tracking-[-0.015em]">{title}</h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{body}</p>
          <div className="mt-5 flex flex-col gap-2">
            {primary && (
              <Button variant="accent" onClick={primary.onClick}>
                {primary.label}
              </Button>
            )}
            {secondary && (
              <Button variant="ghost" onClick={secondary.onClick}>
                {secondary.label}
              </Button>
            )}
            {/* Sign out is always LAST and always present. Every refusal screen carries an
                escape — the rule left behind by the lockout this file documents. */}
            {tertiary && (
              <Button variant="ghost" onClick={tertiary.onClick}>
                <LogOut size={14} />
                {tertiary.label}
              </Button>
            )}
          </div>
        </Panel>
      </div>
    </main>
  );
}
